'use client';

import { useEffect, useMemo } from 'react';
import { useUnit } from 'effector-react';
import { generalEnv } from '@nx-frontend/shared/env';
import { useRouter, usePathname } from '@nx-frontend/shared/hooks/router-proxy';
import { $currentOrganization } from '@nx-frontend/shared/store/current-organization/current-organization';
import { $currentWorkspace } from '@nx-frontend/shared/store/current-workspace';
import { Org } from '@nx-frontend/shared/types';
import { useAppNavHistory } from '@nx-frontend/shared/utils/use-app-nav-history';

const availableSections = [
  'account',
  'notifications',
  'subscription',
  'password',
  'two-steps-auth',
  'evernote',
  'language',
  'appearance',
  'editor',
  'backups',
];

export function Settings() {
  const [currentWorkspace, currentOrganization] = useUnit([$currentWorkspace, $currentOrganization]);
  const router = useRouter();
  const pathname = usePathname() || '';
  const history = useAppNavHistory(pathname);

  useEffect(function () {
    const onWindowPostMessage = function onWindowPostMessage(message: MessageEvent) {
      if (message.data === 'back') {
        const prevUrl = history[history.length - 2];
        if (prevUrl && !/^\/space\/.+\/settings\/?$/i.test(prevUrl)) {
          router.push(prevUrl);
        } else {
          // fallback to default back, if prevUrl is not available or is settings page
          router.back();
        }
      }
    };
    window.addEventListener('message', onWindowPostMessage);
    return function () {
      window.removeEventListener('message', onWindowPostMessage);
    };
  });

  const settingsSection = useMemo(() => {
    const path = pathname;
    // replace the last slash with nothing and extract the latest part of the path
    const lastPart = path.replace(/\/$/, '').split('/').pop();
    let section = availableSections[0];
    if (lastPart && availableSections.includes(lastPart)) {
      section = lastPart;
    }
    return section;
  }, [pathname]);

  const getSettingsPageUrl = (organization: Org) => {
    if (!organization) {
      return '';
    }
    const { domain, sub } = organization || {};
    let baseUrl = generalEnv.nimbusDomain;
    if (sub) {
      baseUrl = `${sub}.${baseUrl}`;
    }
    if (domain) {
      baseUrl = domain;
    }
    const protocol = `https`;
    return `${protocol}://${baseUrl}/ws/${currentWorkspace.globalId}/settings/${settingsSection}?useOldClient=1`;
  };

  return (
    <div className="w-full h-full">
      <iframe
        src={getSettingsPageUrl(currentOrganization)}
        className="user-settings-frame w-full h-full"
        title="settings"
      ></iframe>
    </div>
  );
}
